import React, { useState } from 'react';
import { Baby } from 'lucide-react';
import { SEO } from '../components/SEO';

interface PregnancyState {
  lastPeriod: string;
  cycleLength: string;
  calculationType: 'period' | 'conception' | 'ultrasound';
  ultrasoundDate?: string;
  ultrasoundWeeks?: string;
}

function KalkulatorTrudnoce() {
  const [values, setValues] = useState<PregnancyState>({
    lastPeriod: '',
    cycleLength: '28',
    calculationType: 'period'
  });

  const [results, setResults] = useState<{
    dueDate: Date | null;
    currentWeek: number;
    trimester: number;
    conceptionDate: Date | null;
  } | null>(null);

  const calculateDates = () => {
    let conceptionDate: Date | null = null;
    let dueDate: Date | null = null;

    if (values.calculationType === 'period' && values.lastPeriod) {
      const lastPeriod = new Date(values.lastPeriod);
      const cycleLength = parseInt(values.cycleLength) || 28;
      
      // Conception typically occurs ~14 days before the next period
      const ovulationDays = cycleLength - 14;
      conceptionDate = new Date(lastPeriod);
      conceptionDate.setDate(conceptionDate.getDate() + ovulationDays);
      
      // Due date is 280 days from last period
      dueDate = new Date(lastPeriod);
      dueDate.setDate(dueDate.getDate() + 280);
    } else if (values.calculationType === 'conception' && values.lastPeriod) {
      conceptionDate = new Date(values.lastPeriod);
      
      // Due date is 266 days from conception
      dueDate = new Date(conceptionDate);
      dueDate.setDate(dueDate.getDate() + 266);
    } else if (values.calculationType === 'ultrasound' && 
               values.ultrasoundDate && 
               values.ultrasoundWeeks) {
      const ultrasoundDate = new Date(values.ultrasoundDate);
      const weeksAtUltrasound = parseFloat(values.ultrasoundWeeks);
      
      // Calculate conception date based on ultrasound
      conceptionDate = new Date(ultrasoundDate);
      conceptionDate.setDate(conceptionDate.getDate() - (weeksAtUltrasound * 7));
      
      // Calculate due date
      dueDate = new Date(conceptionDate);
      dueDate.setDate(dueDate.getDate() + 266);
    }

    if (dueDate && conceptionDate) {
      const today = new Date();
      const diffTime = Math.abs(today.getTime() - conceptionDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const currentWeek = Math.floor(diffDays / 7) + 2; // Add 2 weeks as pregnancy is counted from last period

      setResults({
        dueDate,
        currentWeek,
        trimester: currentWeek <= 13 ? 1 : currentWeek <= 26 ? 2 : 3,
        conceptionDate
      });
    }
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('hr-HR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getMilestones = (currentWeek: number): { passed: string[], upcoming: string[] } => {
    const allMilestones = [
      { week: 4, description: 'Implantacija i početak razvoja placente' },
      { week: 6, description: 'Vidljivi prvi otkucaji srca' },
      { week: 8, description: 'Formiranje osnovnih organa' },
      { week: 12, description: 'Kraj prvog tromjesečja' },
      { week: 16, description: 'Moguće osjetiti prve pokrete' },
      { week: 20, description: 'Ultrazvuk morfologije, vidljiv spol' },
      { week: 24, description: 'Beba može čuti zvukove' },
      { week: 28, description: 'Početak trećeg tromjesečja' },
      { week: 32, description: 'Intenzivni razvoj mozga' },
      { week: 36, description: 'Beba se okreće glavom prema dolje' },
      { week: 37, description: 'Trudnoća se smatra terminskom' },
      { week: 40, description: 'Očekivani termin poroda' }
    ];

    return {
      passed: allMilestones
        .filter(m => m.week <= currentWeek)
        .map(m => `${m.week}. tjedan: ${m.description}`),
      upcoming: allMilestones
        .filter(m => m.week > currentWeek)
        .map(m => `${m.week}. tjedan: ${m.description}`)
    };
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Trudnoće",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte termin poroda i pratite razvoj trudnoće. Jednostavan kalkulator s važnim milestones i informacijama o trudnoći.",
    "url": "https://online-kalkulator.com/kalkulator-trudnoce",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun termina poroda",
      "Praćenje razvoja trudnoće",
      "Važni događaji i milestones",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Trudnoće | Izračun Termina Poroda"
        description="Izračunajte termin poroda i pratite razvoj trudnoće. Jednostavan kalkulator s važnim milestones i informacijama o trudnoći."
        canonicalUrl="/kalkulator-trudnoce"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Baby className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Trudnoće</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Calculation Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Način izračuna:
                </label>
                <select
                  value={values.calculationType}
                  onChange={(e) => setValues(prev => ({ 
                    ...prev, 
                    calculationType: e.target.value as PregnancyState['calculationType']
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="period">Prema prvom danu posljednje menstruacije</option>
                  <option value="conception">Prema datumu začeća</option>
                  <option value="ultrasound">Prema ultrazvuku</option>
                </select>
              </div>

              {/* Input Fields */}
              {values.calculationType === 'period' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prvi dan posljednje menstruacije
                    </label>
                    <input
                      type="date"
                      value={values.lastPeriod}
                      onChange={(e) => setValues(prev => ({ ...prev, lastPeriod: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duljina menstrualnog ciklusa (dani)
                    </label>
                    <input
                      type="number"
                      value={values.cycleLength}
                      onChange={(e) => setValues(prev => ({ ...prev, cycleLength: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="npr. 28"
                    />
                  </div>
                </>
              )}

              {values.calculationType === 'conception' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Datum začeća
                  </label>
                  <input
                    type="date"
                    value={values.lastPeriod}
                    onChange={(e) => setValues(prev => ({ ...prev, lastPeriod: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}

              {values.calculationType === 'ultrasound' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Datum ultrazvuka
                    </label>
                    <input
                      type="date"
                      value={values.ultrasoundDate}
                      onChange={(e) => setValues(prev => ({ ...prev, ultrasoundDate: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tjedni trudnoće na ultrazvuku
                    </label>
                    <input
                      type="number"
                      value={values.ultrasoundWeeks}
                      onChange={(e) => setValues(prev => ({ ...prev, ultrasoundWeeks: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="npr. 12"
                      step="0.1"
                    />
                  </div>
                </>
              )}

              {/* Calculate Button */}
              <button
                onClick={calculateDates}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Izračunaj
              </button>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-center py-3 px-4 rounded-lg bg-gray-50">
                    <p className="text-lg text-blue-600">
                      {results.currentWeek}. tjedan trudnoće ({results.trimester}. tromjesečje)
                    </p>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Termin poroda:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {results.dueDate ? formatDate(results.dueDate) : ''}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Datum začeća:</span>
                    <span className="font-semibold">
                      {results.conceptionDate ? formatDate(results.conceptionDate) : ''}
                    </span>
                  </div>

                  {/* Milestones */}
                  {results.currentWeek > 0 && (
                    <div className="space-y-4 pt-4">
                      <h3 className="font-semibold text-gray-700">Prošli događaji:</h3>
                      <ul className="space-y-2 text-gray-600">
                        {getMilestones(results.currentWeek).passed.map((milestone, index) => (
                          <li key={index} className="flex items-center">
                            <span className="text-green-500 mr-2">✓</span>
                            {milestone}
                          </li>
                        ))}
                      </ul>

                      <h3 className="font-semibold text-gray-700">Nadolazeći događaji:</h3>
                      <ul className="space-y-2 text-gray-600">
                        {getMilestones(results.currentWeek).upcoming.map((milestone, index) => (
                          <li key={index} className="flex items-center">
                            <span className="text-gray-400 mr-2">○</span>
                            {milestone}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Trudnoće</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator trudnoće pomaže vam izračunati očekivani termin poroda i prati važne
                  događaje tijekom trudnoće. Možete izračunati termin na temelju prvog dana posljednje menstruacije,
                  datuma začeća ili ultrazvučnog pregleda.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako se Računa Trudnoća</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Trudnoća traje približno 40 tjedana (280 dana) od prvog dana posljednje menstruacije</li>
                  <li>Ili 38 tjedana (266 dana) od začeća</li>
                  <li>Podijeljena je na tri tromjesečja po 13 tjedana</li>
                  <li>Termin se može preciznije odrediti ultrazvučnim pregledom</li>
                  <li>Većina beba se rađa u razdoblju od 37. do 42. tjedna</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Odaberite način izračuna koji vam najviše odgovara</li>
                  <li>Unesite tražene podatke (datum posljednje menstruacije, začeća ili ultrazvuka)</li>
                  <li>Kliknite "Izračunaj" za dobivanje rezultata</li>
                  <li>Pregledajte važne događaje i milestones u trudnoći</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračun je približan i može odstupati od stvarnog termina</li>
                  <li>Samo 5% beba se rodi točno na izračunati datum</li>
                  <li>Ultrazvučni pregled daje najpreciznije rezultate</li>
                  <li>Redovito posjećujte ginekologa tijekom trudnoće</li>
                  <li>Konzultirajte se s liječnikom o svim nedoumicama</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Znakovi Trudnoće</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izostanak menstruacije</li>
                  <li>Mučnina i povraćanje (posebno ujutro)</li>
                  <li>Osjetljivost i napetost grudi</li>
                  <li>Učestalo mokrenje</li>
                  <li>Umor i promjene raspoloženja</li>
                  <li>Promjene apetita i želje za određenom hranom</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorTrudnoce;